/* eslint-disable react/require-default-props */
// import dropboxLogo from '@src/assets/platform-logos/dropbox-logo.png';
// import googleDriveLogo from '@src/assets/platform-logos/google-drive-logo.png';
import zoomLogo from '@src/assets/platform-logos/zoom-logo.png';
import ItemDropdownSelect from '@src/components/common/ItemDropdownSelect';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { ORIGINAL_VIDEO_LANGUAGE_OPTIONS } from '@src/config/languages';
import useVideoCreatorDataInitialization from '@src/hooks/useVideoCreatorDataInitialization';
import { useZoomService } from '@src/hooks/useZoomService';
import { useDatoPerson, useStore } from '@src/stores-v2/StoreContext';
import CircleArrowRightIcon from '@src/svgs/CircleArrowRightIcon';
import LongArrowUpIcon from '@src/svgs/LongArrowUpIcon';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import React, { useEffect, useId, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import DragAndDrop from '../../common/DragAndDrop';
import MultiFileUploadConcat from '../../common/MultiFileUploadConcat';
import { OptionItem, OptionList } from '../../common/OptionList';
import PrimaryActionButton from '../../sidepanel/PrimaryActionButton';
import { getStoryNameFromFileName } from '../utils';
import BRollUploading from './BRollUploading';
import ZoomAuthButton from './ZoomAuthButton';
import ZoomRecordingsList from './ZoomRecordingsList';

import type {
  ArtifactFile,
  ArtifactUpload,
  StoryVideoMusicStrategy,
  ZoomRecordingData,
} from './useStoryCreator';

const logger = LogManager.getLogger('component:StepOne');

type StoryTypeOption = {
  value: 'produced' | 'raw';
  text: string;
};

const STORY_TYPE_OPTIONS: StoryTypeOption[] = [
  {
    value: 'produced',
    text: 'I have produced content',
  },
  {
    value: 'raw',
    text: 'I have raw content',
  },
];

type StoryVideoMusicStrategyOption = {
  value: StoryVideoMusicStrategy;
  text: string;
};

const STORY_VIDEO_MUSIC_STRATEGY_OPTIONS_PRODUCED: StoryVideoMusicStrategyOption[] =
  [
    {
      value: 'process',
      text: 'Remove music',
    },
    {
      value: 'keep',
      text: 'Keep my music',
    },
  ];

const STORY_VIDEO_MUSIC_STRATEGY_OPTIONS_RAW: StoryVideoMusicStrategyOption[] =
  [
    {
      value: 'process',
      text: 'Correct audio',
    },
    {
      value: 'keep',
      text: 'Leave audio',
    },
  ];

const MUSIC_OPTIONS_TITLE: Record<'raw' | 'produced', string> = {
  produced: 'Music',
  raw: 'Audio',
};

const MUSIC_OPTIONS_LIST: Record<
  'raw' | 'produced',
  StoryVideoMusicStrategyOption[]
> = {
  produced: STORY_VIDEO_MUSIC_STRATEGY_OPTIONS_PRODUCED,
  raw: STORY_VIDEO_MUSIC_STRATEGY_OPTIONS_RAW,
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
  gap: 8px;
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: var(--font-weight-bold);
  font-size: 32px;
  line-height: 38.73px;
  text-align: center;
  color: var(--foreground);
  margin: 0;
`;

const Subheading = styled.h3`
  font-weight: var(--font-weight-regular);
  font-size: var(--text-sm);
  line-height: 17px;
  text-align: center;
  color: var(--text-secondary);
  margin: 0;
`;

const ProceedButton = styled(PrimaryActionButton)`
  width: 100%;
  font-weight: var(--font-weight-bold);
  font-size: var(--text-sm);
  line-height: 17px;
`;

const UploadArea = styled.div<{ padded?: boolean }>`
  padding: ${props => (props.padded ? '8px 0' : '0')};
`;

const Hint = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  > .upload-msg {
    color: var(--text-secondary);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-bold);
    line-height: 140%;
  }
  > .upload-limits {
    color: var(--destructive);
    line-height: 140%;
  }
`;

const FieldSet = styled.div`
  margin: 0 0 16px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`;

const FieldName = styled.div`
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: normal;
`;

const Input = styled.input`
  border-radius: 8px;
  border: 1px solid var(--border);
  padding: 16px;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-style: normal;
  font-weight: var(--font-weight-regular);
  line-height: 140%;
  background: var(--background);
`;

const DropdownWrapper = styled.div`
  color: var(--text-secondary);
  .Selected {
    padding: 16px;
  }
`;

const CharacterCount = styled.div<{ isError: boolean }>`
  color: ${props =>
    props.isError ? 'var(--destructive)' : 'var(--text-muted)'};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  margin-top: 4px;
  text-align: right;
`;

const UploadModeSelector = styled.div``;

const UrlInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid var(--border);
  padding: 12px 16px;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-style: normal;
  font-weight: var(--font-weight-regular);
  line-height: 140%;
  background: var(--background);

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-hover);
  }
`;

const UrlInputField = styled.input`
  flex: 1;
  padding: 16px 0;
  border: none;
  outline: none;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-style: normal;
  font-weight: var(--font-weight-regular);
  line-height: 140%;
  background: var(--background);

  &::placeholder {
    color: var(--text-muted);
  }
`;

const ProceedIconButton = styled.button`
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;

  img {
    height: 20px;
    width: auto;
    object-fit: contain;
    opacity: 0.6;
  }
`;

const Divider = styled.div`
  position: relative;
  text-align: center;
  margin: 24px 0;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--border);
  }

  span {
    position: relative;
    background: var(--background);
    padding: 0 16px;
    color: var(--text-muted);
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

const ZoomOptionTitle = styled.div`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  img {
    height: 16px;
    width: auto;
  }
`;

const BackButton = styled.button`
  background: none;
  padding: 12px;
  border: none;
  cursor: pointer;
  position: absolute;
  top: 10px;
  left: 12px;
  transform: rotate(-90deg);
`;

const ConnectButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 16px;
  background: transparent;
  color: var(--text-muted);
  border: 1px solid var(--text-muted);
  border-radius: 6px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  line-height: 140%;
  cursor: pointer;
  transition: all var(--transition-normal);

  &:hover:not(:disabled) {
    background: var(--primary-tint-light);
    border-color: var(--primary-hover);
    color: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--overlay-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--surface-subtle);
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  border: 1px solid var(--border);
`;

const ModalTitle = styled.h3`
  color: var(--foreground);
  font-size: 18px;
  font-weight: var(--font-weight-semibold);
  margin: 0 0 12px 0;
`;

const ModalText = styled.p`
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 24px 0;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ secondary?: boolean; danger?: boolean }>`
  padding: 8px 20px;
  border-radius: 6px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-normal);
  border: 1px solid;

  ${props =>
    props.secondary &&
    `
    background: transparent;
    color: var(--text-secondary);
    border-color: var(--border);

    &:hover {
      background: var(--popover);
      border-color: var(--text-muted);
    }
  `}

  ${props =>
    props.danger &&
    `
    background: transparent;
    color: var(--destructive);
    border-color: var(--destructive);

    &:hover {
      background: color-mix(in srgb, var(--destructive) 10%, transparent);
      border-color: var(--destructive);
    }
  `}
`;

export function StepOne({
  goToNextStep,
  canGoToStep,
  handleStoryVideoUpload,
  handleStoryUrlUpload,
  setStoryName,
  setZoomRecordingData,
  maxUploadSizeGB,
}: {
  goToNextStep: () => void;
  canGoToStep: boolean;
  handleStoryVideoUpload: (f: File[]) => void;
  handleStoryUrlUpload?: (url: string) => void;
  setStoryName?: (s: string) => void;
  setZoomRecordingData?: (data: ZoomRecordingData | null) => void;
  maxUploadSizeGB: number;
}) {
  useVideoCreatorDataInitialization();

  const { userIdentityStore } = useStore();
  const datoPerson = useDatoPerson();
  const {
    enableMultiFileConcat,
    enableUrlUpload,
    enableZoomConnection,
    storyManagerAddStoriesEnableButtons,
  } = useFlagsCombination(userIdentityStore.currentIdentity?.role as any);

  const zoomService = useZoomService();
  const [uploadMode, setUploadMode] = useState<'single' | 'multi'>('single');
  const [urlInput, setUrlInput] = useState<string>('');
  const [urlError, setUrlError] = useState<string>('');
  const [showZoomRecordings, setShowZoomRecordings] = useState<boolean>(false);
  const [isZoomAuthenticated, setIsZoomAuthenticated] =
    useState<boolean>(false);
  const [selectedZoomRecording, setSelectedZoomRecording] = useState<any>(null);
  const [showDisconnectModal, setShowDisconnectModal] =
    useState<boolean>(false);

  // Track if user explicitly disconnected to prevent auto re-auth
  const [explicitlyDisconnected, setExplicitlyDisconnected] =
    useState<boolean>(false);

  // Track the personId that actually has the zoom connection (from OAuth)
  const [zoomPersonId, setZoomPersonId] = useState<string | null>(null);

  // Reset explicitlyDisconnected when component mounts (modal opens)
  useEffect(() => {
    setExplicitlyDisconnected(false);
  }, []);

  // Check if Zoom is already authenticated
  useEffect(() => {
    // Check for OAuth callback params first
    const urlParams = new URLSearchParams(window.location.search);
    const urlPersonId = urlParams.get('personId');

    // Skip auth check if user explicitly disconnected or already authenticated
    if (explicitlyDisconnected || isZoomAuthenticated) {
      return;
    }

    // Check Zoom authentication status via backend API
    const checkZoomAuth = async () => {
      const effectivePersonId =
        urlPersonId || zoomPersonId || datoPerson?.person?.id;
      const email =
        userIdentityStore.currentIdentity?.email || datoPerson?.person?.email;

      // If we have a URL personId, update zoomPersonId state to track it
      if (urlPersonId && urlPersonId !== zoomPersonId) {
        setZoomPersonId(urlPersonId);
      }

      if (isZoomAuthenticated) {
        return;
      }

      if (effectivePersonId || email) {
        const result = await zoomService.checkAuth(effectivePersonId, email);

        if (result.ok) {
          setIsZoomAuthenticated(result.value.authenticated);
        }
      }
    };

    checkZoomAuth();
  }, [
    datoPerson?.person?.id,
    userIdentityStore.currentIdentity?.email,
    explicitlyDisconnected,
    isZoomAuthenticated,
  ]);

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleProceed = async () => {
    // If a Zoom recording is selected, process it
    if (selectedZoomRecording) {
      await processZoomRecording();
    }
    // If URL upload is enabled and URL is provided, use URL upload
    else if (enableUrlUpload && urlInput.trim()) {
      setUrlError('');
      if (!validateUrl(urlInput)) {
        setUrlError('Please enter a valid URL');
        return;
      }
      if (handleStoryUrlUpload) {
        handleStoryUrlUpload(urlInput);
      }
    } else {
      // Otherwise proceed with file upload
      goToNextStep();
    }
  };

  const handleZoomRecordingSelect = async (recording: any) => {
    // Store the selected recording
    setSelectedZoomRecording(recording);
    // Set story name from recording topic (same as video file upload behavior)
    if (setStoryName && recording.topic) {
      setStoryName(getStoryNameFromFileName(recording.topic, 120));
    }
  };

  const processZoomRecording = async () => {
    if (!selectedZoomRecording) return;

    // Set Zoom metadata for the upload pipeline to use
    // The backend will resolve the Zoom OAuth token server-side
    if (setZoomRecordingData) {
      setZoomRecordingData({
        recordingFiles: selectedZoomRecording.recording_files,
        personId: zoomPersonId || undefined,
        email: userIdentityStore.currentIdentity?.email,
        password: selectedZoomRecording.password,
      });
    }

    setShowZoomRecordings(false);
    setSelectedZoomRecording(null);

    // Use handleStoryUrlUpload with a placeholder URL
    // The actual Zoom download will be resolved server-side
    if (handleStoryUrlUpload) {
      handleStoryUrlUpload('zoom-import://pending');
    }
  };

  const handleZoomAuthSuccess = (authData?: {
    personId?: string;
    email?: string;
  }) => {
    // Store the personId that has the zoom connection
    if (authData?.personId) {
      setZoomPersonId(authData.personId);
    }

    setIsZoomAuthenticated(true);
    // Don't automatically show recordings, let user click the button
  };

  return (
    <Wrapper>
      <TitleWrapper>
        <Title>
          {showZoomRecordings ? (
            <>
              Import from <img src={zoomLogo} alt="Zoom" width={120} />
            </>
          ) : (
            'Upload your video'
          )}
        </Title>
        {uploadMode === 'multi' && (
          <Subheading>Combine multiple videos into a single video</Subheading>
        )}
      </TitleWrapper>
      {storyManagerAddStoriesEnableButtons &&
        !showZoomRecordings &&
        enableMultiFileConcat && (
          <UploadModeSelector>
            <OptionList>
              <OptionItem
                fontSize="14px"
                isCurrent={uploadMode === 'single'}
                onClick={() => setUploadMode('single')}
              >
                Single Video
              </OptionItem>
              <OptionItem
                fontSize="14px"
                isCurrent={uploadMode === 'multi'}
                onClick={() => setUploadMode('multi')}
              >
                Multiple Videos
              </OptionItem>
            </OptionList>
          </UploadModeSelector>
        )}
      {storyManagerAddStoriesEnableButtons && !showZoomRecordings && (
        <UploadArea padded>
          {uploadMode === 'single' ? (
            <DragAndDrop
              width={440}
              height={120}
              callback={handleStoryVideoUpload}
              acceptType="video"
              multiple={false}
              videoFormats={['video/mp4', 'video/quicktime']}
              inputTestId="story-video-input"
              description={
                <Hint>
                  <span className="upload-msg">Drop your video file here</span>
                  <span
                    className="upload-limits"
                    style={{ fontSize: '12px', color: 'var(--text-muted)' }}
                  >
                    {`Max file size is ${maxUploadSizeGB} GB`}
                  </span>
                </Hint>
              }
            />
          ) : (
            <MultiFileUploadConcat
              onFilesChange={handleStoryVideoUpload}
              maxUploadSizeGB={maxUploadSizeGB}
            />
          )}
        </UploadArea>
      )}

      {storyManagerAddStoriesEnableButtons && enableUrlUpload && (
        <>
          {!showZoomRecordings || !enableZoomConnection ? (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <UrlInputWrapper>
                  <svg
                    style={{
                      width: '20px',
                      height: '20px',
                      fill: 'var(--text-muted)',
                    }}
                    viewBox="0 0 24 24"
                  >
                    <path d="M10.59 13.41c.41.39.41 1.03 0 1.42-.39.39-1.03.39-1.42 0a5.003 5.003 0 0 1 0-7.07l3.54-3.54a5.003 5.003 0 0 1 7.07 0 5.003 5.003 0 0 1 0 7.07l-1.49 1.49c.01-.82-.12-1.64-.4-2.42l.47-.48a2.982 2.982 0 0 0 0-4.24 2.982 2.982 0 0 0-4.24 0l-3.53 3.53a2.982 2.982 0 0 0 0 4.24m2.82-4.24c.39-.39 1.03-.39 1.42 0a5.003 5.003 0 0 1 0 7.07l-3.54 3.54a5.003 5.003 0 0 1-7.07 0 5.003 5.003 0 0 1 0-7.07l1.49-1.49c-.01.82.12 1.64.4 2.42l-.47.48a2.982 2.982 0 0 0 0 4.24 2.982 2.982 0 0 0 4.24 0l3.53-3.53a2.982 2.982 0 0 0 0-4.24 2.982 2.982 0 0 0-1.42 0z" />
                  </svg>
                  <UrlInputField
                    type="url"
                    placeholder="Or drop a link from Zoom, Google Drive, or Dropbox"
                    value={urlInput}
                    onChange={e => {
                      setUrlInput(e.target.value);
                      setUrlError('');
                    }}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        handleProceed();
                      }
                    }}
                  />
                  <ProceedIconButton
                    disabled={!urlInput.trim()}
                    onClick={handleProceed}
                  >
                    <CircleArrowRightIcon
                      fillColor={
                        !urlInput.trim()
                          ? 'var(--text-muted)'
                          : 'var(--primary)'
                      }
                    />
                  </ProceedIconButton>
                </UrlInputWrapper>
                {urlError && (
                  <div
                    style={{
                      color: 'var(--destructive)',
                      fontSize: '12px',
                      marginTop: '8px',
                    }}
                  >
                    {urlError}
                  </div>
                )}
              </div>

              {enableZoomConnection && (
                <>
                  <Divider>
                    <span>or</span>
                  </Divider>

                  <div style={{ marginTop: '20px' }}>
                    {!isZoomAuthenticated ? (
                      <ZoomAuthButton onAuthSuccess={handleZoomAuthSuccess} />
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          width: '100%',
                        }}
                      >
                        <ConnectButton
                          onClick={() => setShowZoomRecordings(true)}
                          style={{ flex: 1, height: '60px' }}
                        >
                          View Recordings
                          <img
                            src={zoomLogo}
                            alt="Zoom"
                            style={{
                              height: '20px',
                              width: '90px',
                              marginLeft: '4px',
                              flex: 0,
                            }}
                          />
                        </ConnectButton>
                        <ConnectButton
                          onClick={() => setShowDisconnectModal(true)}
                          style={{
                            flex: 0.4,
                            borderColor: 'var(--destructive)',
                            color: 'var(--destructive)',
                            height: '60px',
                          }}
                        >
                          Disconnect
                        </ConnectButton>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div>
              <div
                style={{
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <ZoomOptionTitle>Select a recording</ZoomOptionTitle>
              </div>
              <ZoomRecordingsList
                onSelectRecording={handleZoomRecordingSelect}
                refreshTrigger={showZoomRecordings}
                zoomPersonId={zoomPersonId || undefined}
              />
              <BackButton
                onClick={() => {
                  setShowZoomRecordings(false);
                  setSelectedZoomRecording(null);
                }}
              >
                <LongArrowUpIcon width="16" height="16" />
              </BackButton>
            </div>
          )}
        </>
      )}
      <ProceedButton
        onClick={handleProceed}
        disabled={
          !storyManagerAddStoriesEnableButtons ||
          (!canGoToStep &&
            !(enableUrlUpload && urlInput.trim()) &&
            !selectedZoomRecording)
        }
        isActivated
        style={{ marginTop: '8px' }}
      >
        {selectedZoomRecording
          ? 'Next'
          : enableUrlUpload && urlInput.trim()
            ? 'Import'
            : 'Next'}
      </ProceedButton>

      {showDisconnectModal && (
        <ModalOverlay onClick={() => setShowDisconnectModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>Disconnect Zoom Account?</ModalTitle>
            <ModalText>
              Are you sure you want to disconnect your Zoom account? You'll need
              to reconnect to import recordings again.
            </ModalText>
            <ModalButtons>
              <ModalButton
                secondary
                onClick={() => setShowDisconnectModal(false)}
              >
                Cancel
              </ModalButton>
              <ModalButton
                danger
                onClick={async () => {
                  // CRITICAL: Use zoomPersonId (the person with zoom connection) not datoPerson
                  const personIdToDisconnect =
                    zoomPersonId || datoPerson?.person?.id;

                  const result = await zoomService.disconnect(
                    personIdToDisconnect,
                    userIdentityStore.currentIdentity?.email,
                  );

                  if (result.ok) {
                    setIsZoomAuthenticated(false);
                    setShowZoomRecordings(false);
                    setSelectedZoomRecording(null);
                    setExplicitlyDisconnected(true);
                    setZoomPersonId(null); // Clear the zoom person

                    // Clear any lingering URL params
                    const currentUrl = new URL(window.location.href);
                    if (currentUrl.searchParams.has('personId')) {
                      currentUrl.searchParams.delete('personId');
                      currentUrl.searchParams.delete('zoom_auth');
                      window.history.replaceState(
                        {},
                        '',
                        currentUrl.toString(),
                      );
                    }
                  } else {
                    logger.error(
                      'Failed to disconnect Zoom:',
                      result.error.message,
                    );
                  }

                  setShowDisconnectModal(false);
                }}
              >
                Disconnect
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </Wrapper>
  );
}

export function StepTwo({
  goToNextStep,
  canGoToStep,
  storyType,
  setStoryType,
  videoMusicStrategy,
  setVideoMusicStrategy,
  audioEnhancementDisabled = false,
}: {
  goToNextStep: () => void;
  canGoToStep: boolean;
  storyType: 'raw' | 'produced' | null;
  setStoryType: (t: 'raw' | 'produced') => void;
  videoMusicStrategy: StoryVideoMusicStrategy;
  setVideoMusicStrategy: (s: StoryVideoMusicStrategy) => void;
  audioEnhancementDisabled?: boolean;
}) {
  const enhancementTooltipId = useId();
  return (
    <>
      <Title>Story Details</Title>
      <FieldSet>
        <FieldName>Story Type</FieldName>
        <OptionList>
          {STORY_TYPE_OPTIONS.map(o => (
            <OptionItem
              isCurrent={o.value === storyType}
              onClick={() => setStoryType(o.value)}
              key={o.value}
            >
              {o.text}
            </OptionItem>
          ))}
        </OptionList>
      </FieldSet>
      {storyType && (
        <FieldSet>
          <FieldName>{MUSIC_OPTIONS_TITLE[storyType]}</FieldName>
          <OptionList>
            {MUSIC_OPTIONS_LIST[storyType].map(o => {
              const isDisabled =
                audioEnhancementDisabled && o.value === 'process';
              return (
                <OptionItem
                  isCurrent={o.value === videoMusicStrategy}
                  disabled={isDisabled}
                  onClick={() => setVideoMusicStrategy(o.value)}
                  data-tooltip-id={
                    isDisabled ? enhancementTooltipId : undefined
                  }
                  data-tooltip-content={
                    isDisabled
                      ? 'This file is too large for audio enhancement.'
                      : undefined
                  }
                  key={o.value}
                >
                  {o.text}
                </OptionItem>
              );
            })}
          </OptionList>
          {audioEnhancementDisabled && (
            <Tooltip id={enhancementTooltipId} style={MODAL_TOOLTIP_STYLE} />
          )}
        </FieldSet>
      )}
      <ProceedButton onClick={goToNextStep} disabled={!canGoToStep} isActivated>
        Next
      </ProceedButton>
    </>
  );
}

export function StepThree({
  goToNextStep,
  canGoToStep,
  storyName,
  setStoryName,
  storyLanguage,
  setStoryLanguage,
  allowLangSelect = false,
  maxStoryTitleLength,
  showFolderPicker = false,
  folderOptions = [],
  selectedFolderId = null,
  setSelectedFolderId,
}: {
  goToNextStep: () => void;
  canGoToStep: boolean;
  storyName: string;
  setStoryName: (s: string) => void;
  storyLanguage: string | undefined;
  setStoryLanguage: (s: string) => void;
  allowLangSelect?: boolean;
  maxStoryTitleLength: number;
  showFolderPicker?: boolean;
  folderOptions?: { caption: React.ReactNode; value: string | null }[];
  selectedFolderId?: string | null;
  setSelectedFolderId?: (id: string | null) => void;
}) {
  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (canGoToStep) {
          goToNextStep();
        }
      }}
    >
      <Title>Story Details</Title>
      <FieldSet>
        <FieldName>Story Name</FieldName>
        <Input
          required
          placeholder="Name your story"
          value={storyName}
          onKeyDown={e => e.stopPropagation()}
          onChange={e => setStoryName(e.target.value)}
          maxLength={maxStoryTitleLength}
        />
        <CharacterCount isError={storyName.length > maxStoryTitleLength}>
          {storyName.length}/{maxStoryTitleLength} characters
        </CharacterCount>
      </FieldSet>
      {allowLangSelect && (
        <FieldSet
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <FieldName>Transcript Language</FieldName>
          <DropdownWrapper>
            <ItemDropdownSelect
              action={(_, value) => value && setStoryLanguage(value)}
              propertyName="storyLanguage"
              defaultValue={storyLanguage}
              values={ORIGINAL_VIDEO_LANGUAGE_OPTIONS}
            />
          </DropdownWrapper>
        </FieldSet>
      )}
      {showFolderPicker && (
        <FieldSet
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <FieldName>Add to Folder</FieldName>
          <DropdownWrapper>
            <ItemDropdownSelect
              action={(_, value) => setSelectedFolderId?.(value)}
              propertyName="folder"
              defaultValue={selectedFolderId}
              values={folderOptions}
            />
          </DropdownWrapper>
        </FieldSet>
      )}
      <ProceedButton isActivated disabled={!canGoToStep} type="submit">
        Save
      </ProceedButton>
    </form>
  );
}

export function StepFour({
  goToNextStep,
  handleStoryBRollUpload,
  storyArtifactsFiles,
  storyArtifacts,
  storyArtifactsVideoFiles,
  storyArtifactsVideo,
  saveArtifactNotes,
  setArtifactNotes,
  deleteArtifact,
  saveArtifactVideoNotes,
  setArtifactVideoNotes,
  deleteArtifactVideo,
}: {
  goToNextStep: () => void;
  handleStoryBRollUpload: (f: File[]) => void;
  storyArtifactsFiles: ArtifactFile[];
  storyArtifactsVideoFiles: ArtifactFile[];
  storyArtifacts: ArtifactUpload[];
  storyArtifactsVideo: ArtifactUpload[];
  saveArtifactNotes: (internalId: string) => void;
  setArtifactNotes: (internalId: string, notes: string) => void;
  deleteArtifact: (internalId: string) => void;
  saveArtifactVideoNotes: (internalId: string) => void;
  setArtifactVideoNotes: (internalId: string, notes: string) => void;
  deleteArtifactVideo: (internalId: string) => void;
}) {
  const isNextButtonActive =
    storyArtifactsFiles.length === storyArtifacts.length &&
    storyArtifactsVideoFiles.length === storyArtifactsVideo.length;
  return (
    <>
      <Title>Let&apos;s collect BRoll</Title>
      {storyArtifactsFiles.length === 0 &&
      storyArtifactsVideoFiles.length === 0 ? (
        <UploadArea padded>
          <DragAndDrop
            width={440}
            callback={handleStoryBRollUpload}
            acceptType="all"
            multiple
            description={
              <Hint>
                <span className="upload-msg">
                  Or upload your videos and photos here.
                </span>
                <span className="upload-limits">
                  (Max file size is 100 MB per file)
                </span>
              </Hint>
            }
          />
        </UploadArea>
      ) : (
        <BRollUploading
          handleStoryBRollUpload={handleStoryBRollUpload}
          storyArtifactsFiles={storyArtifactsFiles}
          storyArtifactsVideoFiles={storyArtifactsVideoFiles}
          storyArtifacts={storyArtifacts}
          storyArtifactsVideo={storyArtifactsVideo}
          saveArtifactNotes={saveArtifactNotes}
          setArtifactNotes={setArtifactNotes}
          deleteArtifact={deleteArtifact}
          saveArtifactVideoNotes={saveArtifactVideoNotes}
          setArtifactVideoNotes={setArtifactVideoNotes}
          deleteArtifactVideo={deleteArtifactVideo}
        />
      )}
      <ProceedButton
        onClick={isNextButtonActive ? goToNextStep : () => {}}
        disabled={!isNextButtonActive}
      >
        Next
      </ProceedButton>
    </>
  );
}
